/**
 * Round DMS-IssueRound-1 (2026) — Issue 2: Excel ranges pasted into a DMS
 * rich-text field must become (and stay) a real table.
 *
 * Why this module exists
 * ----------------------
 * Every DMS rich-text field is a react-quill editor. react-quill 2.0.0
 * bundles its OWN Quill 1.3.7 (node_modules/react-quill/node_modules/quill);
 * the root `quill` package is a different instance whose registry the
 * editor never looks at — so everything here is registered on
 * `ReactQuill.Quill`, never on `import Quill from "quill"`.
 *
 * Quill 1.3.7 has no table format: its clipboard flattens a pasted Excel
 * range into paragraphs and the `formats` whitelist drops anything it does
 * not know. This module adds:
 *
 *   1. `dmsTable` — a BlockEmbed blot that owns a bordered <table
 *      class="dms-rt-table">. Its VALUE is a plain cell matrix
 *      ({ rows: [{ header, cells: [text…] }] }); the DOM is built from
 *      that matrix with text nodes only, so no markup from the clipboard
 *      ever reaches the document (the existing Quill format whitelist is
 *      not weakened — it is extended by exactly this one, data-driven
 *      blot). `value(node)` reads a table back into the same matrix, which
 *      is how a saved document round-trips (react-quill → clipboard.convert
 *      → Parchment.query matches the TABLE tag / class → this blot).
 *
 *   2. a capture-phase paste listener that recognises tabular clipboard
 *      data (text/html <table> from Excel / Sheets, or tab-separated
 *      text/plain) and inserts it as ONE dmsTable embed at the caret. A
 *      single copied cell is inserted as plain text (never a 1×1 table),
 *      and non-tabular pastes fall through to Quill's normal handling.
 *
 * The emitted HTML (`quill.root.innerHTML`, which is what react-quill hands
 * to onChange) therefore contains a genuine <table>, which is stored as-is,
 * rendered by the preview modal, and printed by the PDF templates.
 *
 * Known limitation (documented in the release notes): the table is an
 * atomic block — cells are not edited in place; delete the table and paste
 * the corrected range from the spreadsheet again.
 */
import ReactQuill from "react-quill";
import {
    matrixFromClipboardData,
    matrixFromTableElement,
    isSingleCell,
    nonEmptyCellCount,
} from "./clipboardTable";

const Quill = ReactQuill.Quill;

export const DMS_TABLE_BLOT = "dmsTable";
export const DMS_TABLE_CLASS = "dms-rt-table";

/** Accepts { rows: [{header, cells}] } or a bare string[][] and normalises. */
export function normaliseTableValue(value) {
    let rows = [];
    if (value && Array.isArray(value.rows)) rows = value.rows;
    else if (Array.isArray(value)) rows = value;
    const out = rows
        .map((r) => {
            if (Array.isArray(r)) return { header: false, cells: r.map((c) => String(c ?? "")) };
            if (r && Array.isArray(r.cells)) {
                return { header: Boolean(r.header), cells: r.cells.map((c) => String(c ?? "")) };
            }
            return null;
        })
        .filter(Boolean);
    const width = out.reduce((w, r) => Math.max(w, r.cells.length), 0);
    out.forEach((r) => { while (r.cells.length < width) r.cells.push(""); });
    return { rows: out };
}

function appendCellText(cell, text) {
    const lines = String(text ?? "").split("\n");
    lines.forEach((line, i) => {
        if (i > 0) cell.appendChild(document.createElement("br"));
        cell.appendChild(document.createTextNode(line));
    });
}

let registered = false;

/** Idempotent: registers the blot on the Quill instance react-quill uses. */
export function registerDmsTableBlot() {
    if (registered) return;
    if (!Quill) return;
    const BlockEmbed = Quill.import("blots/block/embed");

    class DmsTableBlot extends BlockEmbed {
        static create(value) {
            const node = super.create();
            const { rows } = normaliseTableValue(value);
            node.setAttribute("border", "1");
            node.setAttribute("cellspacing", "0");
            node.setAttribute("cellpadding", "4");
            // Atomic block: the editor never lets the caret inside the table,
            // so DOM edits can never diverge from the stored matrix.
            node.setAttribute("contenteditable", "false");
            const hasHeader = rows.some((r) => r.header);
            const thead = hasHeader ? document.createElement("thead") : null;
            const tbody = document.createElement("tbody");
            rows.forEach((row) => {
                const tr = document.createElement("tr");
                row.cells.forEach((text) => {
                    const cell = document.createElement(row.header ? "th" : "td");
                    appendCellText(cell, text);
                    tr.appendChild(cell);
                });
                (row.header && thead ? thead : tbody).appendChild(tr);
            });
            if (thead && thead.childNodes.length) node.appendChild(thead);
            node.appendChild(tbody);
            return node;
        }

        static value(node) {
            const matrix = matrixFromTableElement(node);
            return matrix ? normaliseTableValue(matrix) : { rows: [] };
        }
    }
    DmsTableBlot.blotName = DMS_TABLE_BLOT;
    DmsTableBlot.tagName = "TABLE";
    DmsTableBlot.className = DMS_TABLE_CLASS;

    Quill.register(DmsTableBlot, true);
    registered = true;
}

/** Adds the blot name to a react-quill `formats` whitelist (immutable). */
export function withDmsTableFormat(formats) {
    if (!Array.isArray(formats)) return formats;           // undefined = "allow everything"
    const clean = formats.filter((f) => typeof f === "string" && f.length > 0);
    return clean.includes(DMS_TABLE_BLOT) ? clean : [...clean, DMS_TABLE_BLOT];
}

/**
 * Insert a matrix at the caret as a dmsTable embed (or as plain text when
 * it is a single cell). Returns true when something was inserted.
 */
export function insertMatrixAtSelection(quill, matrix) {
    if (!quill || !matrix || nonEmptyCellCount(matrix) === 0) return false;
    const Delta = Quill.import("delta");
    const range = quill.getSelection(true) || { index: Math.max(0, quill.getLength() - 1), length: 0 };
    const index = range.index;

    if (isSingleCell(matrix)) {
        const text = matrix.rows[0].cells[0];
        quill.updateContents(
            new Delta().retain(index).delete(range.length).insert(text),
            "user"
        );
        quill.setSelection(index + text.length, 0, "silent");
        return true;
    }

    const value = normaliseTableValue(matrix);
    // A block embed inserted mid-line makes Quill split the paragraph, so
    // the table always lands on its own line with a paragraph after it.
    quill.updateContents(
        new Delta().retain(index).delete(range.length).insert({ [DMS_TABLE_BLOT]: value }),
        "user"
    );
    quill.setSelection(index + 1, 0, "silent");
    return true;
}

/**
 * Attach the Excel/TSV paste interception to a Quill instance. Listens in
 * the CAPTURE phase on the editor container, so it runs before Quill's own
 * paste handler; when it consumes the paste it calls preventDefault(), which
 * makes Quill skip its handler (Quill checks e.defaultPrevented). Anything
 * that is not tabular is left entirely to Quill.
 */
export function attachDmsTablePaste(quill) {
    if (!quill || !quill.container) return () => {};
    const container = quill.container;
    if (container.__dmsTablePasteHandler) return container.__dmsTablePasteHandler.detach;

    const onPaste = (e) => {
        try {
            if (!quill.isEnabled()) return;
            if (!e.clipboardData) return;
            const matrix = matrixFromClipboardData(e.clipboardData);
            if (!matrix) return;                                   // not tabular → Quill default
            if (nonEmptyCellCount(matrix) === 0) return;           // blank range → ignore
            e.preventDefault();
            e.stopImmediatePropagation();
            insertMatrixAtSelection(quill, matrix);
        } catch (err) {
            // Never break typing/pasting — fall back to Quill's own handling.
            // eslint-disable-next-line no-console
            console.warn("[DMS rich text] table paste handler failed; default paste used.", err);
        }
    };
    container.addEventListener("paste", onPaste, true);
    const detach = () => {
        container.removeEventListener("paste", onPaste, true);
        delete container.__dmsTablePasteHandler;
    };
    container.__dmsTablePasteHandler = { detach };
    return detach;
}

export default registerDmsTableBlot;
